import { useState, useRef, useEffect } from 'react';
import { u as useIsMounted } from './hook-Bex3s64q.js';
import { u as usePreviousValue } from './hook-Asj8mXsM.js';

const SWITCH_STATE_EXITED = 'exited';
const SWITCH_STATE_ENTER_BEGIN = 'enterBegin';
const SWITCH_STATE_ENTER_ACTIVE = 'enterActive';
const SWITCH_STATE_ENTER_END = 'enterEnd';
const SWITCH_STATE_LEAVE_BEGIN = 'leaveBegin';
const SWITCH_STATE_LEAVE_ACTIVE = 'leaveActive';
const SWITCH_STATE_LEAVE_END = 'leaveEnd';

const switchStateConfig = {
    [SWITCH_STATE_EXITED]: {
        index: 0,
        description: 'isExited',
        isOnActive: true,
    },
    [SWITCH_STATE_ENTER_BEGIN]: {
        index: 1,
        description: 'isEnterBegin',
        isOnActive: true,
    },
    [SWITCH_STATE_ENTER_ACTIVE]: {
        index: 2,
        description: 'isEnterActive',
        isOnActive: true,
    },
    [SWITCH_STATE_ENTER_END]: {
        index: 3,
        description: 'isEnterEnd',
        hasDelay: true,
        isOnActive: true,
    },
    [SWITCH_STATE_LEAVE_BEGIN]: {
        index: 4,
        description: 'isLeaveBegin',
        isOnActive: false,
    },
    [SWITCH_STATE_LEAVE_ACTIVE]: {
        index: 5,
        description: 'isLeaveActive',
        isOnActive: false,
    },
    [SWITCH_STATE_LEAVE_END]: {
        index: 6,
        description: 'isLeaveEnd',
        hasDelay: true,
        isOnActive: false,
    },
};
const switchStateOrderList = (Object.keys(switchStateConfig)
    .filter((value) => (Object.prototype.hasOwnProperty.call(switchStateConfig, value)))
    .reduce((carry, switchState) => {
    const { index } = switchStateConfig[switchState];
    const newOrderList = [...carry];
    newOrderList[index] = switchState;
    return newOrderList;
}, []));

const getNextSwitchState = state => (switchStateOrderList[(switchStateConfig[state].index + 1) % switchStateOrderList.length]);

const useDelayedSwitchToggle = ({ isOn, waitMs }) => {
    const isMounted = useIsMounted();
    const [isInitialized, setIsInitialized] = useState(false);
    const switchTimerIdListRef = useRef([]);
    const switchReqAnimIdListRef = useRef([]);
    const switchToStateRef = useRef(null);
    const [switchState, setSwitchState] = useState(SWITCH_STATE_EXITED);
    const onIsOnChangeRef = useRef(null);
    if (!onIsOnChangeRef.current) {
        onIsOnChangeRef.current = () => {
            if (isInitialized) {
                return;
            }
            setIsInitialized(true);
        };
    }
    if (!switchToStateRef.current) {
        switchToStateRef.current = (nextState, delayMs) => {
            switchReqAnimIdListRef.current.push(requestAnimationFrame(() => {
                if (delayMs === undefined) {
                    setSwitchState(nextState);
                    return;
                }
                switchTimerIdListRef.current.push(window.setTimeout(() => {
                    setSwitchState(nextState);
                }, delayMs));
            }));
        };
    }
    usePreviousValue({
        value: isOn,
        onChangeRef: onIsOnChangeRef,
    });
    useEffect(() => {
        const { current: switchToState } = switchToStateRef;
        if (!isMounted || !isInitialized || !switchToState) {
            return undefined;
        }
        const { clearTimeout, cancelAnimationFrame } = window;
        const { current: switchTimerIdList } = switchTimerIdListRef;
        const { current: switchReqAnimIdList } = switchReqAnimIdListRef;
        const { isOnActive, hasDelay = false } = switchStateConfig[switchState];
        if (isOn !== isOnActive) {
            return undefined;
        }
        switchToState(getNextSwitchState(switchState), hasDelay ? waitMs : undefined);
        return () => {
            for (const timerId of switchTimerIdList) {
                clearTimeout(timerId);
            }
            for (const reqAnimId of switchReqAnimIdList) {
                cancelAnimationFrame(reqAnimId);
            }
            switchTimerIdListRef.current = [];
            switchReqAnimIdListRef.current = [];
        };
    }, [
        isOn,
        waitMs,
        isMounted,
        isInitialized,
        switchState,
    ]);
    return {
        description: switchStateConfig[switchState].description,
        isInitialized,
        isExited: switchState === SWITCH_STATE_EXITED,
    };
};

export { SWITCH_STATE_ENTER_ACTIVE as S, SWITCH_STATE_ENTER_END as a, SWITCH_STATE_EXITED as b, SWITCH_STATE_LEAVE_ACTIVE as c, SWITCH_STATE_LEAVE_BEGIN as d, SWITCH_STATE_LEAVE_END as e, useDelayedSwitchToggle as u };
//# sourceMappingURL=hook-DK05wrow.js.map
